import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdNotifications,
  MdWarning,
  MdRefresh,
  MdStar,
  MdStarBorder,
  MdExpandMore,
  MdExpandLess,
  MdFeedback,
  MdPerson,
  MdDateRange,
  MdInfo,
  MdChat,
  MdLaunch,
  MdSmartToy,
  MdChecklist,
  MdSchool
} from 'react-icons/md';
import { apiCall } from '../utils/api/ApiUtils';
import { useClass } from '../utils/ClassContext';
import FeedbackApi from '../utils/api/FeedbackApi';

const Notifications = ({ username, onNavigate, onImmediateRefresh, decrementNotificationCount, rollbackNotificationCount }) => {
  const [notifications, setNotifications] = useState([]);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [feedbackDetails, setFeedbackDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingFeedback, setLoadingFeedback] = useState(new Set());

  const usernameRef = useRef(username);
  const { getClassContext } = useClass();

  // Update username ref when it changes
  usernameRef.current = username;

  // Define fetchNotifications function that uses the ref
  const fetchNotifications = useCallback(async () => {
    const currentUsername = usernameRef.current;
    if (!currentUsername) return;

    setLoading(true);
    setError('');

    try {
      const data = await apiCall(`/notifications/${currentUsername}`);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notifications on component mount and when username changes
  useEffect(() => {
    fetchNotifications();
  }, [username, fetchNotifications]);

  const triggerImmediateRefresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Expose the refresh function to parent
  useEffect(() => {
    if (onImmediateRefresh) {
      onImmediateRefresh.current = triggerImmediateRefresh;
    }
  }, [triggerImmediateRefresh, onImmediateRefresh]);


  const handleViewNotification = async (notification) => {
    const notificationId = notification.notification_id;

    try {
      // Mark notification as seen if it's unseen - OPTIMISTIC UPDATE
      if (notification.status === 'unseen') {
        // 1. Update UI and counts immediately (optimistic)
        setNotifications(prev =>
          prev.map(n =>
            n.notification_id === notificationId
              ? { ...n, status: 'seen' }
              : n
          )
        );
        
        // Optimistically decrement notification count
        if (decrementNotificationCount) {
          decrementNotificationCount(1);
        }

        try {
          // 2. Update server in background
          await apiCall(`/notifications/${notificationId}/seen`, { method: 'POST' });
          // Server update successful - UI is already updated
        } catch (error) {
          console.error('Error marking notification as seen:', error);
          
          // 3. Rollback optimistic updates on server failure
          setNotifications(prev =>
            prev.map(n =>
              n.notification_id === notificationId
                ? { ...n, status: 'unseen' }
                : n
            )
          );
          
          // Rollback notification count
          if (rollbackNotificationCount) {
            rollbackNotificationCount(1);
          }
          
          alert('Failed to mark notification as read. Please try again.');
          return; // Don't expand notification if server failed
        }
      }

      // Toggle expansion
      setExpandedNotifications(prev => {
        const newSet = new Set(prev);
        if (newSet.has(notificationId)) {
          newSet.delete(notificationId);
        } else {
          newSet.add(notificationId);
          // If it's a feedback notification and we haven't loaded details yet, fetch them
          if (notification.type === 'feedback' && notification.specifying_id && !feedbackDetails[notificationId]) {
            fetchFeedbackDetails(notification.specifying_id, notificationId);
          }
        }
        return newSet;
      });
    } catch (error) {
      console.error('Error processing notification:', error);
      alert('Error processing notification');
    }
  };

  const fetchFeedbackDetails = async (feedbackId, notificationId) => {
    setLoadingFeedback(prev => new Set(prev).add(notificationId));

    try {
      // Get class context for API call (same pattern as FeedbackLog)
      const classContext = getClassContext();
      const data = await FeedbackApi.getFeedbackDetails(feedbackId, classContext);
      setFeedbackDetails(prev => ({
        ...prev,
        [notificationId]: data.feedback
      }));
    } catch (error) {
      console.error('[ERROR] Error fetching feedback details:', error);
      alert('Error loading feedback details');
    } finally {
      setLoadingFeedback(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago' }) + ' CT';
  };

  const handleMarkAllAsRead = async () => {
    // Store original state for rollback
    const originalNotifications = [...notifications];
    const unseenNotifications = notifications.filter(n => n.status === 'unseen');
    const unseenCount = unseenNotifications.length;
    
    if (unseenCount === 0) return;

    try {
      // 1. Update UI and counts immediately (optimistic) - don't set loading yet
      setNotifications(prev =>
        prev.map(n => ({ ...n, status: 'seen' }))
      );
      
      // Optimistically decrement notification count by the number of unseen notifications
      if (decrementNotificationCount) {
        decrementNotificationCount(unseenCount);
      }
      
      // 2. Update server in background
      setLoading(true);
      await apiCall(`/notifications/mark-all-read/${username}`, { method: 'POST' });
      
      // Server update successful - UI is already updated
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      
      // 3. Rollback optimistic updates on server failure
      setNotifications(originalNotifications);
      
      // Rollback notification count
      if (rollbackNotificationCount) {
        rollbackNotificationCount(unseenCount);
      }
      
      alert('Failed to mark all notifications as read. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'unseen') {
      return (
        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
      );
    }
    return null;
  };

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case 'ai_feedback':
      case 'feedback':
        return <MdSmartToy className="text-gray-500" />;
      case 'discussion':
        return <MdChat className="text-gray-500" />;
      case 'evaluation':
        return <MdChecklist className="text-gray-500" />;
      case 'environment_release':
        return <MdSchool className="text-gray-500" />;
      default:
        return <MdNotifications className="text-gray-500" />;
    }
  };

  const getNotificationColor = (type) => {
    const baseColors = {
      'ai_feedback': 'bg-white border-l-4 border-l-blue-500 border border-gray-200',
      'feedback': 'bg-white border-l-4 border-l-blue-500 border border-gray-200',
      'discussion': 'bg-white border-l-4 border-l-purple-500 border border-gray-200',
      'evaluation': 'bg-white border-l-4 border-l-orange-500 border border-gray-200',
      'environment_release': 'bg-white border-l-4 border-l-green-500 border border-gray-200',
      'default': 'bg-white border-l-4 border-l-gray-400 border border-gray-200'
    };

    return baseColors[type] || baseColors.default;
  };

  const getNotificationTitle = (type) => {
    switch (type) {
      case 'ai_feedback':
      case 'feedback':
        return 'AI Feedback Available';
      case 'discussion':
        return 'New Discussion Comment';
      case 'evaluation':
        return 'Feedback Evaluated';
      case 'environment_release':
        return 'New Environment Released';
      default:
        return 'Notification';
    }
  };

  const getNotificationMetadata = (notification, details, isExpanded) => {
    if (notification.type === 'feedback' && details && !isExpanded) {
      return (
        <div className="flex items-center space-x-2 mt-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
            {details.environment}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
            {details.relationship}
          </span>
        </div>
      );
    }
    return null;
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      index < rating ? (
        <MdStar key={index} className="text-yellow-400 text-lg" />
      ) : (
        <MdStarBorder key={index} className="text-gray-300 text-lg" />
      )
    ));
  };

  // Enhanced ERD diagram renderer that handles different formats
  const renderERDDiagram = (details) => {

    // Check if we have base64 SVG diagram data (regenerated from backend like Streamlit)
    if (details.erd_diagram) {
      console.log('[DEBUG] Processing erd_diagram data...');
      try {
        // The backend now regenerates SVG from ERD data (like Streamlit), so decode and display
        console.log('[DEBUG] Attempting to decode base64...');
        const decodedSvg = atob(details.erd_diagram);
        console.log('[DEBUG] Decoded SVG length:', decodedSvg.length);
        console.log('[DEBUG] Decoded SVG starts with:', decodedSvg.substring(0, 100));

        if (decodedSvg.includes('<svg')) {
          console.log('[DEBUG] SVG content detected, rendering directly');
          // Display the SVG directly
          return (
            <div className="bg-white p-4 rounded border overflow-auto text-center">
              <div
                dangerouslySetInnerHTML={{ __html: decodedSvg }}
                className="w-full flex justify-center"
              />
            </div>
          );
        } else {
          console.log('[DEBUG] No SVG tags found, trying as image fallback');
          // Fallback: try as image if it's not SVG content
          return (
            <div className="bg-white p-4 rounded border overflow-auto text-center">
              <img
                src={`data:image/svg+xml;base64,${details.erd_diagram}`}
                alt="ERD Diagram"
                className="max-w-full h-auto mx-auto border shadow-sm"
                onError={(e) => {
                  console.error('[ERROR] Failed to load ERD diagram as image');
                  console.error('[ERROR] Image src was:', e.target.src);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="text-red-600 text-sm">
                Failed to load diagram. Data length: {details.erd_diagram?.length || 0}
              </div>
            </div>
          );
        }
      } catch (error) {
        console.error('[ERROR] Error in renderERDDiagram - processing erd_diagram:', error);
        console.error('[ERROR] erd_diagram value was:', details.erd_diagram);
      }
    } else {
      console.log('[DEBUG] No erd_diagram found, checking erd_plain...');
    }

    // Fallback to plain text notation if available
    if (details.erd_plain) {
      console.log('[DEBUG] Using erd_plain fallback');
      console.log('[DEBUG] erd_plain content:', details.erd_plain?.substring(0, 200));
      return (
        <div className="bg-white p-4 rounded border">
          <h5 className="font-medium text-gray-700 text-sm mb-2">ERD Notation:</h5>
          <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
            {details.erd_plain}
          </pre>
        </div>
      );
    }

    // No diagram available
    console.log('[DEBUG] No diagram data available - showing fallback message');
    return (
      <div className="bg-white p-4 rounded border text-center text-gray-500">
        <MdInfo className="text-2xl mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No diagram available</p>
        <div className="text-xs text-gray-400 mt-2">
          Debug: erd_diagram={!!details.erd_diagram}, erd_plain={!!details.erd_plain}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading notifications...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Notifications</h1>
            <p className="text-gray-600">
              View and manage your notifications. Click on notifications to view details and mark them as read.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <MdNotifications className="text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <MdWarning className="text-red-500 text-xl mr-3" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Content */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Your Notifications</h2>
          {notifications.some(n => n.status === 'unseen') && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Mark All as Read
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MdNotifications className="text-6xl mb-4 mx-auto text-gray-400" />
            <p className="text-lg">No notifications found</p>
            <p className="text-sm mt-2">You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Statistics */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-800 mb-3">Notification Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {notifications.length}
                  </div>
                  <div className="text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {notifications.filter(n => n.status === 'seen').length}
                  </div>
                  <div className="text-gray-600">Read</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {notifications.filter(n => n.status === 'unseen').length}
                  </div>
                  <div className="text-gray-600">Unread</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {notifications.filter(n => n.type === 'feedback').length}
                  </div>
                  <div className="text-gray-600">Feedback</div>
                </div>
              </div>
            </div>

            {/* Notifications Cards */}
            <div className="space-y-0 divide-y divide-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 px-1">All Notifications</h3>
              {notifications.map((notification) => {
                const isExpanded = expandedNotifications.has(notification.notification_id);
                const isLoadingDetails = loadingFeedback.has(notification.notification_id);
                const details = feedbackDetails[notification.notification_id];

                return (
                  <div key={notification.notification_id}>
                    {/* Main Notification Container - Modern minimalistic style */}
                    <div
                      onClick={() => handleViewNotification(notification)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-all duration-150 ${getNotificationColor(notification.type)}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="text-lg">{getNotificationTypeIcon(notification.type)}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {getNotificationTitle(notification.type)}
                            </p>
                            <div className="flex items-center gap-2 ml-2">
                              <p className="text-xs text-gray-500">
                                {formatTimestamp(notification.timestamp)}
                              </p>
                              {getStatusBadge(notification.status)}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                            {notification.text}
                          </p>
                          {getNotificationMetadata(notification, details, isExpanded)}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="text-base text-gray-400">
                            {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mx-4 mt-2 border-t border-gray-200 pt-4 pb-2">
                        {isLoadingDetails ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600">Loading details...</span>
                          </div>
                        ) : notification.type === 'feedback' && details ? (
                          <div className="space-y-4">
                            {/* Action Button for Feedback */}
                            <div className="flex justify-end">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onNavigate) {
                                    const navigationData = {
                                      feedbackId: notification.specifying_id,
                                      environment: details.environment,
                                      relationship: details.relationship,
                                      username: details.username,
                                      autoExpand: true
                                    };
                                    onNavigate('discussions', navigationData);
                                  }
                                }}
                                className="inline-flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg text-sm"
                              >
                                <MdChat className="mr-1 text-lg" />
                                Join Discussion
                                <MdLaunch className="ml-1 h-3 w-3" />
                              </button>
                            </div>

                            {/* Basic Info */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="flex items-center gap-2">
                                <MdPerson className="text-blue-600" />
                                <div>
                                  <h4 className="font-medium text-gray-700">Student:</h4>
                                  <p className="text-gray-900">{details.username}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <MdInfo className="text-green-600" />
                                <div>
                                  <h4 className="font-medium text-gray-700">Environment:</h4>
                                  <p className="text-gray-900">{details.environment}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <MdFeedback className="text-purple-600" />
                                <div>
                                  <h4 className="font-medium text-gray-700">Relationship:</h4>
                                  <p className="text-gray-900">{details.relationship}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <MdDateRange className="text-orange-600" />
                                <div>
                                  <h4 className="font-medium text-gray-700">Date:</h4>
                                  <p className="text-gray-900">{formatTimestamp(details.date)}</p>
                                </div>
                              </div>
                            </div>

                            {/* ERD Diagram - Enhanced */}
                            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                              <h4 className="font-medium text-gray-700 mb-2">ERD Diagram:</h4>
                              {renderERDDiagram(details)}
                            </div>

                            {/* Feedback Content */}
                            <div className="border border-gray-300 rounded-lg p-4">
                              <h4 className="font-medium text-gray-700 mb-2">Feedback:</h4>
                              <div className="space-y-2">
                                {details.feedback && details.feedback.split('\n').map((line, index) => (
                                  <p key={index} className="text-gray-800 leading-relaxed">
                                    {line.replace(/'/g, '`')}
                                  </p>
                                ))}
                              </div>
                            </div>

                            {/* Q&A Section */}
                            {details.qa && details.qa.length > 0 && (
                              <div className="border border-gray-300 rounded-lg p-4">
                                <h4 className="font-medium text-gray-700 mb-3">FAQ:</h4>
                                <div className="space-y-2">
                                  {details.qa.map((item, index) => (
                                    <details key={index} className="bg-gray-50 rounded-md">
                                      <summary className="p-3 cursor-pointer font-medium text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                                        {item.question}
                                      </summary>
                                      <div className="p-3 pt-0 text-sm text-gray-600 whitespace-pre-wrap">
                                        {item.answer}
                                      </div>
                                    </details>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Rating Information */}
                            {(details.rating !== undefined && details.rating !== null) || details.comment ? (
                              <div className="border border-gray-300 rounded-lg p-4">
                                <h4 className="font-medium text-gray-700 mb-2">Student Rating:</h4>

                                {details.rating !== undefined && details.rating !== null && (
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className="flex">
                                      {renderStars(details.rating)}
                                    </div>
                                    <span className="text-gray-600">({details.rating}/5)</span>
                                    {details.rating === 0 && (
                                      <span className="text-gray-500 text-sm ml-2">(No rating given)</span>
                                    )}
                                  </div>
                                )}

                                {details.comment ? (
                                  <div>
                                    <h5 className="font-medium text-gray-700 text-sm mb-1">Comment:</h5>
                                    <p className="text-gray-800 text-sm">{details.comment}</p>
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-sm">No comment provided</p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Basic Notification Information */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <h4 className="font-medium text-gray-700 mb-3">Notification Details</h4>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <MdDateRange className="text-blue-600" />
                                  <div>
                                    <span className="font-medium text-gray-700">Date:</span>
                                    <span className="ml-2 text-gray-900">{formatTimestamp(notification.timestamp)}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full ${notification.status === 'unseen' ? 'bg-red-500' : 'bg-green-500'}`}></div>
                                  <div>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <span className="ml-2 text-gray-900 capitalize">{notification.status}</span>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <MdInfo className="text-purple-600 mt-0.5" />
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-700">Message:</span>
                                    <p className="ml-2 text-gray-900 whitespace-pre-wrap">{notification.text}</p>
                                  </div>
                                </div>
                                {notification.type === 'environment_release' && notification.content?.released_by ? (
                                  <div className="flex items-center gap-2">
                                    <MdPerson className="text-green-600" />
                                    <div>
                                      <span className="font-medium text-gray-700">Released by:</span>
                                      <span className="ml-2 text-gray-900">{notification.content.released_by}</span>
                                    </div>
                                  </div>
                                ) : notification.content && notification.type !== 'environment_release' && (
                                  <div className="flex items-start gap-2">
                                    <MdInfo className="text-indigo-600 mt-0.5" />
                                    <div className="flex-1">
                                      <span className="font-medium text-gray-700">Additional Content:</span>
                                      <div className="ml-2 text-gray-900">
                                        {typeof notification.content === 'object' ? (
                                          <div className="space-y-1">
                                            {Object.entries(notification.content).map(([key, value]) => (
                                              <div key={key} className="text-sm">
                                                <span className="font-medium">{key}:</span> {String(value)}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <span>{String(notification.content)}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center">
                              {notification.type === 'discussion' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigate) {
                                      const navigationData = {
                                        feedbackId: notification.specifying_id,
                                        autoExpand: true,
                                        username: notification.content?.student || username  // Fall back to current user if no target student
                                      };
                                      console.log('[Notifications] Discussion navigation data:', navigationData);
                                      console.log('[Notifications] Full notification:', notification);
                                      onNavigate('discussions', navigationData);
                                    }
                                  }}
                                  className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                  <MdChat className="mr-2" />
                                  View Discussion
                                </button>
                              )}
                              {notification.type === 'evaluation' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigate) {
                                      const navigationData = {
                                        feedbackId: notification.specifying_id,
                                        autoExpand: true,
                                        username: notification.content?.student || username  // Fall back to current user if no target student
                                      };
                                      console.log('[Notifications] Evaluation navigation data:', navigationData);
                                      console.log('[Notifications] Full notification:', notification);
                                      onNavigate('discussions', navigationData);
                                    }
                                  }}
                                  className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                  <MdChecklist className="mr-2" />
                                  View Evaluation
                                </button>
                              )}
                              {notification.type === 'environment_release' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigate) {
                                      const navigationData = {
                                        environment: notification.content?.environment || notification.specifying_id,
                                        autoSelect: true
                                      };
                                      console.log('[Notifications] Environment navigation data:', navigationData);
                                      console.log('[Notifications] Full notification:', notification);
                                      onNavigate('editor');
                                    }
                                  }}
                                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                  <MdSchool className="mr-2" />
                                  View Environment
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <button
          onClick={fetchNotifications}
          className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md transition-colors duration-200 flex items-center gap-2 mx-auto"
        >
          <MdRefresh className="text-lg" />
          Refresh Notifications
        </button>
      </div>
    </div>
  );
};

export default Notifications;